import { Link } from "react-router-dom";
import { ArrowRight, GraduationCap, Award, Users } from "lucide-react";
import SEO from "@/components/SEO";
import { ATTORNEY } from "@/data/content";

export default function About() {
    return (
        <>
            <SEO
                title="About Joseph G. Helfrick"
                description="Joseph G. Helfrick is a Sacramento personal injury attorney. Born and raised in Sacramento. Holy Cross College of Notre Dame (B.A. History, 2010). Lincoln Law School (J.D., 2015 — Faculty Achievement Award). California State Bar (2017). U.S. District Court, Eastern District of California (2020)."
                path="/about"
                breadcrumbs={[
                    { name: "Home", path: "/" },
                    { name: "About Joe", path: "/about" },
                ]}
            />

            {/* HEADER */}
            <section
                data-testid="about-hero"
                className="border-b border-[#1F3458]"
            >
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 pb-16 lg:pt-28 lg:pb-24 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-7">
                        <div className="text-[#D4AF37] text-xs uppercase tracking-[0.28em] font-bold">
                            About the Attorney
                        </div>
                        <h1 className="mt-6 font-serif text-5xl lg:text-7xl text-[#F7F5EB] leading-[0.95] font-light">
                            Joseph G. <span className="italic">Helfrick</span>
                        </h1>
                        <p className="mt-6 text-lg text-[#9FB3D4] max-w-2xl leading-relaxed">
                            Sacramento native. Lincoln Law honors graduate.
                            Years of trial experience on both sides of the
                            personal-injury aisle. Now standing exclusively with
                            the injured.
                        </p>
                        <div
                            className="tldr mt-8 max-w-2xl border-l-2 border-[#D4AF37] pl-5 text-[#F7F5EB]/85 text-sm leading-relaxed"
                            data-speakable
                        >
                            <span className="block text-[#D4AF37] text-[10px] uppercase tracking-[0.28em] font-bold mb-2">
                                Quick Answer
                            </span>
                            Joseph G. Helfrick is a Sacramento-born personal
                            injury attorney serving Sacramento County and Placer
                            County, California. He earned a B.A. in History from
                            Holy Cross College of Notre Dame (2010) and a J.D.
                            from Lincoln Law School (2015). Admitted to the
                            California State Bar in 2017 and the U.S. District
                            Court for the Eastern District of California in
                            2020.
                        </div>
                    </div>
                    <div className="lg:col-span-5 relative">
                        <div className="relative aspect-[3/4] max-w-sm ml-auto border border-[#1F3458]">
                            <img
                                src="/images/joe-helfrick-portrait.jpg"
                                alt="Joseph G. Helfrick portrait"
                                className="absolute inset-0 w-full h-full object-cover object-center"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* BIO */}
            <section
                data-testid="about-bio"
                className="border-b border-[#1F3458]"
            >
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-4">
                        <div className="text-[#D4AF37] text-xs uppercase tracking-[0.28em] font-bold">
                            Biography
                        </div>
                        <h2 className="mt-4 font-serif text-3xl lg:text-4xl text-[#F7F5EB] leading-tight">
                            From Sacramento — to Notre Dame — and back.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 lg:col-start-6 space-y-6 text-[#F7F5EB]/90 text-base lg:text-lg leading-relaxed">
                        <p>{ATTORNEY.bio}</p>
                        <p className="text-[#9FB3D4]">
                            Joe attended Jesuit High School in Sacramento before
                            heading east to Holy Cross College of Notre Dame,
                            where he earned a Bachelor of Arts in History in
                            2010. He returned home to attend Lincoln Law School,
                            graduating in 2015 with the Faculty Achievement
                            Award for the highest grade in Legal Analysis. He
                            has tried cases throughout Northern California and
                            handles matters in both state and federal court.
                        </p>
                    </div>
                </div>
            </section>

            {/* CREDENTIALS GRID */}
            <section
                data-testid="about-creds"
                className="border-b border-[#1F3458] bg-[#0B1A36]"
            >
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24">
                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="bg-[#11203D] border border-[#1F3458] p-8 md:p-10">
                            <GraduationCap
                                size={28}
                                className="text-[#D4AF37]"
                            />
                            <h3 className="mt-5 font-serif text-2xl text-[#F7F5EB]">
                                Education
                            </h3>
                            <ul className="mt-5 space-y-4 text-sm text-[#9FB3D4]">
                                {ATTORNEY.education.map((e) => (
                                    <li key={e.school}>
                                        <div className="text-[#F7F5EB] font-medium">
                                            {e.school}
                                        </div>
                                        <div>
                                            {e.degree}
                                            {e.year ? ` · ${e.year}` : ""}
                                        </div>
                                        {e.note && (
                                            <div className="text-[#D4AF37] text-xs mt-1">
                                                {e.note}
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="bg-[#11203D] border border-[#1F3458] p-8 md:p-10 md:mt-10">
                            <Award size={28} className="text-[#D4AF37]" />
                            <h3 className="mt-5 font-serif text-2xl text-[#F7F5EB]">
                                Admissions
                            </h3>
                            <ul className="mt-5 space-y-3 text-sm text-[#9FB3D4]">
                                {ATTORNEY.admissions.map((a) => (
                                    <li
                                        key={a}
                                        className="text-[#F7F5EB]/90"
                                    >
                                        {a}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="bg-[#11203D] border border-[#1F3458] p-8 md:p-10 md:mt-20">
                            <Users size={28} className="text-[#D4AF37]" />
                            <h3 className="mt-5 font-serif text-2xl text-[#F7F5EB]">
                                Memberships
                            </h3>
                            <ul className="mt-5 space-y-3 text-sm text-[#9FB3D4]">
                                {ATTORNEY.memberships.map((m) => (
                                    <li key={m} className="text-[#F7F5EB]/90">
                                        {m}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section data-testid="about-cta">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 grid lg:grid-cols-12 gap-12 items-end">
                    <div className="lg:col-span-7">
                        <h2 className="font-serif text-4xl lg:text-6xl text-[#F7F5EB] leading-tight">
                            Ready to talk?{" "}
                            <span className="italic text-[#D4AF37]">
                                Joe answers.
                            </span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5">
                        <Link
                            to="/contact"
                            data-testid="about-cta-link"
                            className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#07142B] font-bold uppercase tracking-wider text-sm px-8 py-4 hover:bg-[#B8962A] transition-colors"
                        >
                            Request a Free Consultation{" "}
                            <ArrowRight size={16} />
                        </Link>
                        <div className="mt-5">
                            <Link
                                to="/case-evaluator"
                                data-testid="about-cta-evaluator"
                                className="text-[#9FB3D4] text-sm underline underline-offset-4 decoration-[#1F3458] hover:text-[#D4AF37] hover:decoration-[#D4AF37] transition-colors"
                            >
                                Prefer to start online? Take the 60-second
                                case evaluator →
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
